import React, { useCallback, useEffect, useState } from 'react'
import TextField from 'components/atoms/TextField/TextField'
import VatIcon from 'components/atoms/VatIcon/VatIcon'
import styles from './PercentageField.module.scss'
import PercentageInput from 'views/components/atoms/TextField/PercentageInput'
import PercentageVATCalculator from 'components/molecules/PercentageVATCalculator/PercentageVATCalculator'
import { ITextField, OnOff } from 'components/atoms/TextField/text-field.types'

interface IPercentageFieldProps
  extends Omit<
    ITextField,
    | 'textarea'
    | 'leadingIcon'
    | 'trailingIcon'
    | 'onLeadingIconClick'
    | 'onTrailingIconClick'
    | 'type'
    | 'placeholder'
    | 'inputComponent'
  > {
  includeVatControl?: boolean
  includeVatCalculator?: boolean
  vatApplied?: boolean
  onToggleChange?: (vatApplied: boolean, value?: number) => void
  amount?: string | number
  value?: string | number
  allowNegative?: boolean
  autoComplete?: OnOff
  placeholder?: string
}

const PercentageField: React.FC<IPercentageFieldProps> = ({
  id,
  className,
  disabled,
  label,
  name,
  error,
  noOutline,
  dark,
  includeVatControl = false,
  includeVatCalculator = true,
  vatApplied = false,
  onToggleChange,
  value,
  allowNegative = false,
  autoComplete,
  placeholder,
  onChange,
  onBlur,
  ...rest
}) => {
  const [currentValue, setCurrentValue] = useState(0)
  const [initialValue, setInitialValue] = useState(0)

  useEffect(() => {
    const floatValue = parseFloat(String(value || 0))
    setCurrentValue(floatValue)
    setInitialValue(floatValue)
  }, [value])

  const handlePercentageChange = (e: any) => {
    const { floatValue } = e
    setCurrentValue(floatValue)
    setInitialValue(floatValue)
    onChange?.(e)
  }

  const handleToggleChange = () => {
    onToggleChange?.(!vatApplied)

    if (vatApplied) {
      setCurrentValue(initialValue)
    }
  }

  const handleBlur = (e: any): void => {
    onBlur?.(e)
  }

  const handleVATCalculatorChange = (includingVat: number) => {
    setCurrentValue(includingVat)
    onChange?.({ floatValue: includingVat })
  }

  const handleVatIconClick = useCallback(() => {
    if (!disabled) handleToggleChange()
  }, [disabled, vatApplied])

  const trailingIcon = !includeVatControl ? null : <VatIcon enabled={vatApplied} onClick={handleVatIconClick} />

  return (
    <div className={styles.root}>
      <TextField
        id={id}
        className={className}
        disabled={disabled}
        label={label}
        name={name}
        error={error}
        noOutline={noOutline}
        dark={dark}
        inputComponent={
          <PercentageInput
            onChange={handlePercentageChange as any}
            onBlur={handleBlur as any}
            value={currentValue}
            placeholder="0%"
            trailingIcon={trailingIcon}
            allowNegative={allowNegative}
            {...(rest as any)}
          />
        }
      />
      {includeVatCalculator && (
        <PercentageVATCalculator
          vatApplied={vatApplied}
          disabled={disabled}
          amount={currentValue}
          onChange={handleVATCalculatorChange}
        />
      )}
    </div>
  )
}

export default PercentageField
